/**
 * Minimal i18n for the meet app. Deliberately not i18next: this app has ~20
 * strings and no backend, and a 40 kB framework would be the single largest
 * dependency on the join path — which is the path we most need to stay fast on
 * a congested Cairo mobile connection.
 *
 * Arabic is the primary language for Everhall students, so the document
 * direction flips to RTL and LiveKit's own prefabs inherit it from <html dir>.
 *
 * ---------------------------------------------------------------------------
 * COPY RULES. Follow these when adding a string; they are why the labels read
 * the way they do, and the app looked improvised before it had them.
 *
 * 1. A CONTROL label is the ACTION the tap performs, in both directions:
 *    "Turn off microphone" while it is on, "Turn on microphone" while it is
 *    off. Never a state ("Microphone") and never the wrong direction — a
 *    button reading "Open chat" while the chat is open is the classic bug.
 *    State is carried by colour + aria-pressed, not by the words.
 * 2. NO POSSESSIVES and no articles in control labels: "Share screen", not
 *    "Share your screen"; "Close", not "Close the list". Short reads modern
 *    and survives a 360 px bar.
 * 3. Verb on the trigger, NOUN on the thing: the button says "Show people",
 *    the panel it opens is titled "In this class".
 * 4. A PANEL or STATUS message is a full, warm sentence — the terseness above
 *    is for controls only. Anything a child reads when something went wrong
 *    says what happened and what to do next, in one sentence.
 * 5. Arabic control labels use the MASDAR (verbal noun): «مشاركة الشاشة»,
 *    «رفع اليد», «فتح الدردشة» — not the imperative «شارك», «ارفع». It is the
 *    standard register for UI labels AND it carries no gender, so a female
 *    teacher or student is never addressed as male. Avoid third-person
 *    references to the teacher for the same reason («معلّمك» → phrase it around).
 * 6. Never blame the user for a device limit: "This device can't share its
 *    screen. A computer can!" — not "sharing failed".
 * ---------------------------------------------------------------------------
 */

import { meetingCopy } from './meetingCopy'

export type Locale = 'ar' | 'en'

const en = {
  'connection.fixing': 'Fixing…',
  'connection.fixNow': 'Fix now',
  'app.title': 'Everhall',
  'boot.preparing': 'Preparing your class…',
  'boot.noLink.title': 'No class link',
  'boot.noLink.detail':
    'Open your class from the Everhall app or website — the join button there brings you here with a fresh link.',
  'prejoin.group': 'Group class',
  'prejoin.solo': 'One-on-one class',
  'prejoin.join': 'Join',
  // The observer's whole prejoin card: no camera, no microphone, just this line
  // so a staff observer knows exactly what joining does — and does not.
  'prejoin.ghost': "You'll watch invisibly — nobody can see or hear you.",
  // Shown to a Ghost who arrived before a teacher opened the room (HTTP 425).
  // The screen retries on its own, so the copy promises exactly that.
  'waitingClass.title': "The class hasn't opened yet",
  'waitingClass.detail': "You'll join automatically as soon as it opens — stay on this screen.",
  'left.title': 'You left the class',
  'left.reconnectFailed': "We couldn't reconnect you",
  'left.detail': 'Rejoin if the class is still running.',
  'left.rejoin': 'Rejoin class',
  'evicted.title': 'You joined on another device',
  // Meetings only (the host's Remove); classes keep the left screen.
  'removed.title': 'You were removed from this class',
  'removed.detail': 'The teacher removed you. Ask them for a new link if this was a mistake.',
  'evicted.detail':
    'This class is now open somewhere else. Close this tab, or rejoin here to move the class back.',
  // Shown instead of "Stay here" when we can see the fault is the student's own
  // device. Says what staying achieves, because after fifteen minutes of a silent
  // child, "stay here" reads as "keep doing nothing".
  'error.retry': 'Try again',
  'error.expired.title': 'This link is no longer valid',
  'error.expired.detail': 'Go back to the Everhall app and press Join again to get a fresh link.',
  'error.notStarted.title': 'Your class has not started yet',
  'error.notStarted.detail':
    'Come back closer to the start time, then press Join in the Everhall app.',
  'error.ended.title': 'This class has ended',
  'error.ended.detail': 'Go back to the Everhall app to see your other classes.',
  'error.network.title': 'Connection problem',
  'error.network.detail': 'Check your internet connection, then try again.',

  // Deliberately asks the user to WAIT. Retrying instantly during the server's
  // cool-off makes the outage worse for everyone behind the same mobile network.
  'error.unavailable.title': 'The class is not ready yet',
  'error.unavailable.detail': 'Please wait about half a minute, then try again.',
  'error.unknown.title': 'Something went wrong',
  'error.unknown.detail':
    'Try again — if it keeps failing, go back to the Everhall app and press Join.',
  'error.reference': 'Reference',
  // Prejoin: identity is shown, never typed.
  'prejoin.joining': 'Joining…',
  'prejoin.camera': 'Camera',
  'prejoin.microphone': 'Microphone',
  'prejoin.speaker': 'Speaker',
  'prejoin.speakerTest': 'Test',
  'prejoin.speakerPlaying': 'Playing…',
  'prejoin.default': 'System default',
  'prejoin.cameraOff': 'Your camera is off',
  'prejoin.blocked': 'Camera blocked',
  // Never "reload": the join code was already scrubbed from the URL on boot, so
  // reloading here lands on "No class link". Recovery stays on THIS screen —
  // allow access, then Try again (re-runs the preview) or Join with camera off.
  'prejoin.blockedHelp':
    'Your browser is blocking the camera and microphone. Allow them from the address bar, then tap Try again — or tap Join to enter with your camera off.',

  // Microphone readiness. A child reads these while a lesson is either about to
  // start or already running, so every line says what happened and what to do
  // next, and none of them imply the child did something wrong.
  'prejoin.noCamera':
    "This device doesn't have a camera — that's fine. Your teacher will still hear you.",
  'prejoin.hostMicFault':
    'Students will not hear you until your microphone works. You can fix it after joining.',
  'prejoin.hostMicOff': 'Your microphone is off — students will not hear you.',
  'prejoin.hostMicChecking': 'Still checking your microphone…',
  'prejoin.hostMicUnheard':
    'We have not heard you yet — say something, or pick another microphone.',
  'prejoin.fixMic': 'Fix microphone',
  'prejoin.joinAnyway': 'Join anyway',
  'prejoin.fixCamera': 'Fix camera',
  'connection.title': 'Your connection is unstable',
  'connection.body': 'The class may freeze or cut out. Use Fix now to recover the connection.',
  'help.title': 'Check my setup',
  'help.menu': 'Something not working?',
  'help.header': 'Something needs attention — open Check my setup',
  'help.row.mic': 'Microphone',
  'help.row.cam': 'Camera',
  'help.row.speaker': 'Speakers',
  'help.row.share': 'Screen share',
  'help.row.connection': 'Connection',
  'help.row.pip': 'Floating window',
  'help.fix': 'Fix',
  'help.testTone': 'Play test sound',
  'help.copyCode': 'Copy support code',
  'help.readCode': 'Read this code to support: {code}',
  'help.ok': 'Ready to use',
  'help.mic.muted': 'You’re muted',
  'help.mic.blocked': 'The browser is not allowing your microphone',
  'help.mic.silent': 'Your microphone is not sending sound',
  'help.cam.off': 'Your camera is off',
  'help.cam.blocked': 'The browser is not allowing your camera',
  'help.cam.frozen': 'Your camera has stopped sending video',
  'help.speaker.unavailable': 'Speaker selection is not available here',
  'help.speaker.blocked': 'Tap to let the class play sound',
  'help.share.unavailable': 'This device cannot share a screen',
  'help.share.board': 'Close the whiteboard so students can see your screen',
  'help.share.failed': 'Screen sharing needs attention',
  'help.connection.lost': 'The class is reconnecting',
  'help.connection.poor': 'Your connection is weak',
  'help.pip.unavailable': 'Floating window is not available here',
  'help.pip.ready': 'Ready to float the class',
  'help.pip.active': 'Floating ✓',
  'recovery.reconnectingMic': 'Reconnecting your microphone…',
  'mic.check.title': 'Can we hear you?',
  'mic.check.prompt': 'Say hello — the bar should move.',
  'mic.check.heard': 'We can hear you.',
  'mic.check.quiet':
    "We can't hear anything yet. Check the mute button on your headset, or pick another microphone.",
  'mic.check.checking': 'Checking your microphone…',
  'mic.status.blocked': 'Microphone blocked',
  'mic.status.missing': 'No microphone',
  'mic.status.busy': 'Microphone unavailable',
  'mic.status.silent': 'Microphone is silent',
  'mic.status.fix': 'How to fix it',
  'mic.help.titleBlocked': 'The browser is holding the microphone',
  'mic.help.titleMissing': "We can't find a microphone",
  'mic.help.titleBusy': 'Another app is using the microphone',
  'mic.help.titleSilent': 'The microphone is on, but no sound',
  'mic.help.lede': 'Follow the picture, then tap the button below.',
  'mic.help.ledeSilent': 'Check the mute switch on the headset, then tap the button below.',
  'mic.help.retry': 'Try again',
  'mic.help.checking': 'Checking…',
  'mic.help.notNow': 'Not now',
  'mic.help.joinAnyway': 'Join without microphone',
  'mic.help.notYourBrowser': 'Not your browser?',
  'mic.help.stepsLabel': 'Steps',
  'mic.help.askAdult': 'Still stuck? Ask a grown-up to help, or tell your teacher in the chat.',
  'cam.help.titleBlocked': 'Your camera is off',
  'cam.help.titleMissing': "We can't find a camera",
  'cam.help.titleBusy': 'Your camera is busy',
  'cam.help.lede': 'Follow the picture, then tap the button below.',
  'mic.platform.chromeAndroid': 'Chrome on Android',
  'mic.platform.chromiumDesktop': 'Chrome on computer',
  'mic.platform.edgeDesktop': 'Edge on computer',
  'mic.platform.firefoxDesktop': 'Firefox on computer',
  'mic.platform.ios': 'iPhone or iPad',
  'mic.platform.generic': 'Any browser',
  'mic.steps.chromeAndroid.1': 'Tap the three dots at the top of the browser.',
  'mic.steps.chromeAndroid.2': 'Open Site settings.',
  'mic.steps.chromeAndroid.3': 'Set Microphone to Allow.',
  'mic.steps.chromiumDesktop.1': 'Click the icon at the left of the address bar.',
  'mic.steps.chromiumDesktop.2': 'Switch Microphone on.',
  'mic.steps.edgeDesktop.1':
    'Click the icon at the left of the address bar, then Permissions for this site.',
  'mic.steps.edgeDesktop.2': 'Set Microphone to Allow.',
  'mic.steps.firefoxDesktop.1': 'Click the padlock at the left of the address bar.',
  'mic.steps.firefoxDesktop.2': 'Clear the microphone block, then allow it.',
  'mic.steps.ios.1': 'Open Settings, find this browser, and allow the Microphone.',
  'mic.steps.ios.2': 'Come back to this page and tap Retry.',
  'mic.steps.generic.1': 'Open your browser settings for this site.',
  'mic.steps.generic.2': 'Set Microphone to Allow.',
  'mic.steps.missing.1': 'Plug in a microphone or headset.',
  'mic.steps.missing.2': 'Then tap Retry.',
  'mic.steps.busy.1':
    'Close any other app using the microphone, like another calling app or a voice note.',
  'mic.steps.busy.2': 'Then tap Retry.',
  'mic.steps.silent.1': 'Check the little mute button on your headset cable or earphones.',
  'mic.steps.silent.2': 'Pick a different microphone from the list, then say hello.',
  'mic.steps.silent.3':
    'On Windows: open Settings → Privacy → Microphone and make sure apps are allowed to use it.',
  'cam.steps.chromeAndroid.1': 'Tap the three dots at the top of the browser.',
  'cam.steps.chromeAndroid.2': 'Open Site settings.',
  'cam.steps.chromeAndroid.3': 'Set Camera to Allow.',
  'cam.steps.chromiumDesktop.1': 'Click the icon at the left of the address bar.',
  'cam.steps.chromiumDesktop.2': 'Switch Camera on.',
  'cam.steps.edgeDesktop.1':
    'Click the icon at the left of the address bar, then Permissions for this site.',
  'cam.steps.edgeDesktop.2': 'Set Camera to Allow.',
  'cam.steps.firefoxDesktop.1': 'Click the padlock at the left of the address bar.',
  'cam.steps.firefoxDesktop.2': 'Clear the camera block, then allow it.',
  'cam.steps.ios.1': 'Open Settings, find this browser, and allow the Camera.',
  'cam.steps.ios.2': 'Come back to this page and tap Retry.',
  'cam.steps.generic.1': 'Open your browser settings for this site.',
  'cam.steps.generic.2': 'Set Camera to Allow.',
  'cam.steps.missing.1': 'Connect a camera.',
  'cam.steps.missing.2': 'Then tap Retry.',
  'cam.steps.busy.1': 'Close any other app using the camera, like another calling app.',
  'cam.steps.busy.2': 'Then tap Retry.',
  // Teacher-facing. Adults, denser, still never blaming the child.
  'roster.micBlocked': 'Browser is blocking their microphone',
  'roster.micMissing': 'No microphone on their device',
  'roster.micBusy': 'Their microphone is busy or unavailable',
  'roster.micSilent': 'Their microphone is on but no sound is coming through',
  'tile.micBlockedLabel': 'Microphone blocked',
  'tile.micHelpHint':
    'Ask them to allow the microphone for this site, then press Retry on their screen.',
  'prejoin.retry': 'Try again',
  'prejoin.classLabel': 'Class',
  'prejoin.micOn': 'Turn off microphone',
  'prejoin.micOff': 'Turn on microphone',
  'prejoin.camOn': 'Turn off camera',
  'prejoin.camOff': 'Turn on camera',
  'role.student': 'Student',
  'role.teacher': 'Teacher',
  'role.parent': 'Parent',
  'room.recording': 'Recording',
  // Ghost self-indicator, shown ONLY to the observer themselves in the header —
  // a quiet reminder that they are present but hidden from everyone else.
  'room.observing': 'Observing — invisible',
  // In-call
  'room.controls': 'Class controls',
  'more.open': 'More',
  'more.close': 'Close more',
  'more.caption': 'More',
  'more.controls': 'More controls',
  'more.tools': 'Tools',
  'devices.heading': 'Devices',
  'devices.microphone': 'Microphone',
  'devices.camera': 'Camera',
  'devices.speaker': 'Speaker',
  'devices.systemDefault': 'System default',
  'devices.useMic': 'Use microphone: {device}',
  'devices.useCamera': 'Use camera: {device}',
  'devices.useSpeaker': 'Use speaker: {device}',
  'devices.nowUsing': 'Now using {device}.',
  'devices.switchFailed': 'Could not switch devices. Try again.',
  'devices.deviceGone': 'The selected device is no longer available. Using your system default.',
  'more.openHands': 'More — {count} hands raised',
  'more.closeHands': 'Close more — {count} hands raised',
  'more.openUpdates': 'More — updates',
  'more.closeUpdates': 'Close more — updates',
  'room.react': 'Send a reaction',
  'room.reactClose': 'Close reactions',
  'room.inviteLink': 'Invite link',
  'room.copyInvite': 'Copy invite link',
  'room.inviteCopied': 'Link copied',
  'room.raiseHand': 'Raise hand',
  'room.lowerHand': 'Lower hand',
  'room.handRaised': 'Hand raised',
  'room.handsUp': 'Hands raised',
  // In-class chat
  'audio.blocked': 'Tap to hear the class.',
  'audio.unlock': 'Turn on sound',
  'chat.title': 'Class chat',
  'chat.open': 'Open chat',
  'chat.close': 'Close chat',
  'chat.openUnread': 'Open chat — {count} new',
  'chat.placeholder': 'Write a message…',
  'chat.send': 'Send',
  'chat.emoji': 'Add emoji',
  'chat.empty': 'No messages yet — say hello!',
  'chat.earlier': 'Earlier messages',
  'chat.new': 'New messages',
  'chat.removed': 'Removed',
  'chat.removedByYou': 'Removed by you',
  'chat.delete': 'Remove this message',
  'chat.muteUser': 'Turn off their chat',
  'chat.unmuteUser': 'Allow their chat again',
  'chat.mutedChip': 'Chat off',
  'chat.freeze': 'Pause chat',
  'chat.unfreeze': 'Resume chat',
  'chat.frozenHost': 'Chat is paused — only you can write.',
  'chat.frozenStudent': 'The chat is paused for now.',
  'chat.mutedYou': 'Your chat is turned off for now.',
  'chat.phFrozen': 'Chat is paused',
  'chat.phMuted': 'Your chat is off',
  'chat.tooLong': 'That message is too long.',
  'chat.tooFast': 'A little slower — one message at a time.',
  'chat.observer': 'Read-only — you can watch',
  'chat.attach': 'Add a picture or file',
  'chat.imageView': 'Picture',
  'chat.imgTooLarge': 'That picture is too big — try a smaller one.',
  'chat.imgNotImage': 'That is not a picture.',
  'chat.imgTimedOut': 'That took too long — try a smaller picture.',
  'chat.imgUnreadable': 'That picture would not open — try a different one.',
  'chat.imgFailed': 'Could not send that picture — tap to try again.',
  'chat.imgBusy': 'One picture at a time — wait for this one to finish.',
  'chat.imgAnimated': 'Moving pictures cannot be sent — try a photo or a screenshot.',
  'chat.fileTooLarge': 'That file is too big — the limit is 10 MB.',
  'chat.notFile': 'Only pictures and PDF files can be sent.',
  'chat.fileOpen': 'Open file',
  'chat.fileFailed': 'Could not send that file — tap to try again.',
  'chat.fileOpenFailed': 'The file could not be opened — tap it again.',
  'chat.drop': 'Drop pictures or files here',
  'chat.dropHint': 'They will be sent to the class',
  'chat.dropTooMany': 'Only {count} at a time — the rest were not added.',
  'chat.sending': 'Sending {count}…',
  'chat.playOn': 'Play on {site}',
  'chat.opensOutside': 'Opens in a new tab',
  'chat.shareToPlay': 'To play it for the class, share your screen with sound.',
  'chat.search': 'Search messages',
  'chat.searchPlaceholder': 'Search this chat…',
  'chat.searchNone': 'Nothing matches “{term}”.',
  'chat.searchCount': '{count} found',
  'share.asked': 'Could you show your screen to the class?',
  'share.accept': 'Share screen',
  'share.tryAgain': 'Try again',
  'share.close': 'Close',
  'share.errUnfocused': 'Tap “Share screen” again with this lesson in front.',
  'share.errUnsupported': 'This device can’t share its screen. A computer can!',
  'share.errBlocked': 'Something blocked sharing. Close other apps and try again.',
  'share.errSystemDenied': 'Your computer is not allowing screen sharing yet.',
  'share.errBusy': 'Someone else is presenting right now — you can try again when they finish.',
  'share.errFailed': 'That didn’t work. You can try again.',
  'share.otherPresenting': '{name} is presenting',
  'share.takeOver': 'Share instead',
  // Names the SURFACE first, and that is the load-bearing half. Sound only
  // exists on a browser tab: pick "Entire screen" or a window and there is no
  // audio option to find at all (we exclude system audio deliberately — see
  // screenShare.ts), so copy that just said "turn on the sound option" sent a
  // teacher hunting the picker for a control that was never going to be there.
  //
  // The option itself is described rather than quoted: the browser's own label
  // drifts between Chrome and Edge versions ("Also share tab audio", "Share tab
  // audio"), and a wrong quote is worse than a description.
  'share.noSound':
    'This share has no sound. To let the class hear a video, share the browser tab it is playing in and turn on that tab’s sound option.',
  'share.help.title': 'Fix screen sharing',
  'share.help.lede': 'Follow these steps, then try sharing again.',
  'share.help.retry': 'Share screen',
  'share.help.show': 'Show me how',
  'share.help.system.1': 'Open System Settings.',
  'share.help.system.2': 'Open Privacy & Security, then Screen Recording.',
  'share.help.system.3': 'Turn on your browser.',
  'share.help.system.4':
    'Quit and reopen the browser. Your lesson link still works after reopening.',
  'share.help.unfocused': 'Click the lesson window first, then share your screen.',
  'share.help.sound.1': 'Choose Chrome Tab or Browser Tab.',
  'share.help.sound.2': 'Turn on “Also share tab audio”.',
  'share.help.black': 'Close the whiteboard so students can see your screen.',
  'share.help.capture': 'Close other calling or capture apps, then try again.',
  'share.help.capture.generic':
    'Close another app that may be sharing your screen, then try again.',
  'share.soundOn':
    'The class can hear the shared sound. Headphones keep your voice clear while it plays.',
  // Says STOPPED, not muted, because that is what happened: a share cannot be
  // paused, so ending it is the only thing the control does. Forward-looking
  // second half so a child is not left wondering whether they are in trouble.
  'share.stoppedByTeacher':
    'Your screen sharing has stopped. You can share again whenever your class needs it.',
  // The short button labels are room.hideFaces / room.showFaces (already in
  // both languages); these two are the explanatory titles.
  'share.hideFaces': 'Hide the camera strip so the share gets the whole stage',
  'share.showFaces': 'Show the camera strip again',
  'share.fullscreen': 'View the shared screen full screen',
  'share.fullscreenShort': 'Full screen',
  'share.exitFullscreen': 'Exit full screen',
  'share.exitFullscreenShort': 'Exit',
  // One-word captions under the control-bar buttons. Discoverability is the
  // point: users reported not knowing what the icons do. Keep these SHORT —
  // they live under a 52px circle.
  // Every caption must fit its 52px control with IBM Plex Sans Arabic at its
  // size (11px EN / 12px AR) or it ellipsizes into nonsense — which is worse
  // than no caption. That is why these are terse ("Saver", not "Data saver").
  'ctl.mic': 'Mic',
  'ctl.camera': 'Camera',
  'ctl.saver': 'Saver',
  'ctl.bg': 'Effects',
  'ctl.react': 'React',
  'ctl.hand': 'Hand',
  'ctl.share': 'Share',
  'ctl.pip': 'Pop out',
  'ctl.chat': 'Chat',
  'ctl.people': 'People',
  'pip.enter': 'Float the class',
  'pip.exit': 'Close the floating window',
  'pip.title': 'Everhall — your class',
  'pip.emptyTitle': 'Your class is still here',
  'pip.emptyBody': 'No one is on camera right now — you can still hear everything.',
  'pip.loading': 'Video is loading…',
  'pip.steps.chromium.1': 'Click the icon left of the web address, then Site settings.',
  'pip.steps.chromium.2': "Find 'Automatic picture-in-picture' and choose Allow.",
  'pip.steps.generic.1': 'Click Float the class before you switch windows.',
  'pip.autoBlockedTitle': "Your class won't float automatically.",
  'pip.tipTitle': 'Tip: when you switch tabs, your class floats in a small window.',
  'pip.tipBody': 'Switching to another app? Click Float the class first.',
  'pip.tipFloatNow': 'Float now',
  'pip.tipGotIt': 'Got it',
  'moderate.askShare': 'Ask to share screen',
  'moderate.askedShare': 'Asked to share',
  'moderate.shareWaiting': 'Waiting for an answer…',
  'moderate.shareBusy': 'Someone is already presenting',
  'moderate.shareUnavailable': 'Their device can’t share a screen',
  'moderate.shareAccepted': '{name} is sharing',
  'moderate.shareDeclined': '{name} said not now',
  'moderate.shareNoAnswer': 'No answer from {name}',
  'moderate.shareCancelled': '{name} didn’t pick a screen',
  'moderate.shareFailed': 'Sharing didn’t work on their device',
  // Participants panel
  'roster.title': 'In this class',
  'roster.open': 'Show people',
  'roster.hide': 'Hide people',
  'roster.openCount': 'Show people — {count} in class',
  'roster.close': 'Close',
  'roster.handsUp': '{count} waiting to speak',
  'roster.speaking': 'talking now',
  'roster.micOn': 'Microphone on',
  'roster.cameraOn': 'Camera on',
  'room.share': 'Share screen',
  'room.stopShare': 'Stop sharing',
  'room.presenting': 'is presenting',
  'room.celebrate': 'Celebrate',
  'room.wellDone': 'Well done!',
  'bg.title': 'Background',
  'bg.open': 'Change background',
  'bg.close': 'Close backgrounds',
  'bg.none': 'None',
  'bg.blur': 'Blur',
  // The brand name stays Latin in both catalogues — it is a wordmark, not a
  // word, and the tile shows the logo anyway.
  'bg.everhall': 'Everhall',
  'bg.study': 'Study',
  'bg.sky': 'Sky',
  'bg.mint': 'Mint',
  'bg.space': 'Space',
  'bg.applying': 'Applying…',
  'bg.slow': 'Backgrounds may run slowly on this device.',
  // /the disabled overflow entry when the browser cannot run
  // background effects at all — an explained limitation, never a vanished control.
  'bg.unsupported': "Backgrounds aren't supported on this browser.",
  'room.you': 'you',
  'room.leave': 'Leave class',
  'room.leaveTitle': 'Leave the class?',
  'room.leaveBody':
    'Your teacher and classmates will see you go. You can rejoin with the same link while the class is still running.',
  'room.leaveStay': 'Stay in class',
  'room.leaveYes': 'Yes, leave',
  // Meeting host only (ADR 0014): ending for everyone, offered from the leave dialog.
  'room.endForAll': 'End meeting for everyone',
  'room.endTitle': 'End the meeting for everyone?',
  'room.endBody': 'Everyone is disconnected and the invite link stops working.',
  'room.endYes': 'Yes, end it',
  'room.endKeep': 'Keep meeting',
  // The meeting host's lobby panel (ADR 0014). {count} is rendered as the badge.
  'lobby.waiting': '{count} waiting to join',
  'lobby.guest': 'Guest',
  'lobby.admit': 'Admit',
  'lobby.deny': 'Deny',
  'lobby.admitName': 'Admit {name}',
  'lobby.denyName': 'Deny {name}',
  'lobby.admitAll': 'Admit all',
  'room.waitingStudents': 'Waiting for students to join',
  'room.muted': 'Microphone off',
  'room.mutedSpeaking': "You're muted",
  // Generic "background", not "blur": the same toast covers blur AND virtual
  // backgrounds.
  'room.blurStarting': 'Preparing your background…',
  'room.blurFailed': 'The background could not start — your camera is still on',
  'room.hideFaces': 'Hide faces',
  'room.showFaces': 'Show faces',
  // Says WAIT. A child who reads nothing assumes the app is broken and closes it.
  'room.reconnecting': 'Reconnecting… stay on this screen',
  'room.micBanner.host': "Students can't hear you — your microphone isn't working.",
  'room.micBanner.student': "Your teacher can't hear you — your microphone isn't working.",
  'room.fix': 'Fix',
  'room.dismiss': 'Dismiss',
  'saver.control': 'Turn on data saver',
  'saver.controlOn': 'Turn off data saver',
  'saver.controlAuto': 'Data saver came on by itself — tap to see why',
  'saver.onManual': 'Saving data — incoming videos are paused so the sound stays clear.',
  'saver.turnOff': 'Turn off',
  'saver.resume': 'Resume videos',
  'saver.autoL1': 'Saving data — classmates’ videos are paused so the sound stays clear.',
  'saver.autoL2':
    'Saving data — incoming videos are paused so the sound stays clear. They come back when it improves.',
  'saver.cameraStillOn': 'Your camera is still on.',
  'saver.dismiss': 'Hide',
  'saver.pausedTile': 'Video paused to save data',
  'saver.weakPeer': 'Their connection is weak — they can still hear you',
  'tile.connectionIssue': 'connection issue — audio continues.',
  'nc.control': 'Noise cancellation',
  'nc.on': 'Turn off noise cancellation',
  'nc.off': 'Turn on noise cancellation — hides background noise',
  'nc.loading': 'Starting noise cancellation…',
  'nc.failed':
    'Noise cancellation could not start — your normal microphone is being used. Tap to try again.',
  'nc.stateOn': 'On',
  'nc.stateOff': 'Off',
  'nc.stateLoading': 'Starting…',
  'nc.stateFailed': 'Didn’t start',
  'nc.toastOn':
    'Noise cancellation is on — sounds around you (fans, street, family) are filtered out, so students hear only your voice.',
  'nc.toastOnStudent':
    'Noise cancellation is on — sounds around you (fans, street, family) are filtered out of your microphone.',

  // Teacher moderation. The wording carries the asymmetry: "Mute" happens, "Ask"
  // only asks. A teacher must never think they have switched a child's camera on.
  'moderate.for': 'Manage {name}',
  'moderate.remove': 'Remove from class',
  'moderate.muteMic': 'Turn off microphone',
  'moderate.cameraOff': 'Turn off camera',
  'moderate.stopShare': 'Stop their screen share',
  'moderate.askMic': 'Ask to unmute',
  'moderate.askCamera': 'Ask to turn camera on',
  'moderate.askNote':
    'Turning things off is immediate. Turning them back on is the student’s choice.',
  'moderate.askedMic': 'Asked them to unmute',
  'moderate.askedCamera': 'Asked them to turn the camera on',
  'moderate.failed': 'That did not work — try again',

  // The student's side of the same exchange.
  'unmute.askedMic': 'Please turn your microphone on for the class',
  'unmute.askedCamera': 'Please turn your camera on for the class',
  'unmute.turnOnMic': 'Turn on microphone',
  'unmute.turnOnCamera': 'Turn on camera',
  'unmute.failedMic': 'Your microphone would not turn on. Check it is plugged in and allowed.',
  'unmute.failedCamera': 'Your camera would not turn on. Check it is allowed.',
  'unmute.notNow': 'Not now',

  // Shown while a student is alone in the room, waiting.
  'waiting.title': "You're in! Waiting for your teacher…",
  'waiting.sub': 'Your teacher will be here any moment.',
  'waiting.hint1': 'Tip: sit somewhere quiet so everyone can hear you.',
  'waiting.hint2': 'Tip: tap the hand button when you want to answer.',
  'waiting.hint3': 'Tip: keep your notebook and pencil close by.',
  'waiting.hint4': 'Tip: you can send a 👍 or a 🎉 to your class.',

  // Lesson surface: whiteboard + annotate-over-share (teacher tools).
  'surface.board': 'Board',
  'surface.openBoard': 'Open whiteboard',
  'surface.closeBoard': 'Close whiteboard',
  'surface.clearBoard': 'Clear whiteboard',
  'surface.clearShort': 'Clear',
  'surface.boardLoading': 'Loading the board…',
  // Shown in place of the share while its track is not yet delivering frames.
  // Never leave a black rectangle: it reads as the class being broken.
  'surface.shareConnecting': 'Connecting to the shared screen…',
  'surface.viewOnly': 'View only',
  'surface.ink': 'Draw',
  'surface.startInk': 'Draw on the shared screen',
  'surface.stopInk': 'Stop drawing',
  'surface.annotating': 'drawing',
  'surface.pen': 'Pen',
  'surface.highlighter': 'Highlighter',
  'surface.undo': 'Undo',
  'surface.clearInk': 'Clear drawings',
  // Shown to the teacher only. A board that has quietly stopped syncing is far
  // worse than one that says why, so these are never silent.
  'surface.boardTooLarge': 'The board is too full to share — clear it to carry on',
  'surface.boardFailed': 'The board could not be shared — try clearing it',
  'surface.boardFailedToLoad': 'The board could not load. Check the connection and try again.',
  'surface.tools': 'Drawing tools',
  'surface.arrow': 'Arrow',
  'surface.line': 'Line',
  'surface.rect': 'Rectangle',
  'surface.ellipse': 'Circle',
  'surface.eraser': 'Eraser',
  'surface.color': 'Colour',
}

const ar: Record<StringKey, string> = {
  'connection.fixing': 'جارٍ الإصلاح…',
  'connection.fixNow': 'أصلح الآن',
  'app.title': 'Everhall',
  'boot.preparing': 'جارٍ تحضير حصتك…',
  'boot.noLink.title': 'لا يوجد رابط للحصة',
  'boot.noLink.detail':
    'افتح حصتك من تطبيق Everhall أو الموقع — زر الانضمام هناك يفتح لك رابطًا جديدًا.',
  'prejoin.group': 'حصة جماعية',
  'prejoin.solo': 'حصة فردية',
  'prejoin.join': 'انضم',
  'prejoin.ghost': 'مشاهدة خفية — لا يراك ولا يسمعك أحد.',
  'waitingClass.title': 'لم تُفتح الحصة بعد',
  'waitingClass.detail': 'سيتم الانضمام تلقائيًا فور فتح الحصة — ابقَ على هذه الشاشة.',
  'left.title': 'لقد خرجت من الحصة',
  'left.reconnectFailed': 'تعذّر علينا إعادة اتصالك بالحصة',
  'left.detail': 'يمكنك الانضمام مرة أخرى إذا كانت الحصة ما زالت جارية.',
  'left.rejoin': 'انضم مرة أخرى',
  'evicted.title': 'لقد انضممت من جهاز آخر',
  'removed.title': 'تمت إزالتك من هذه الحصة',
  'removed.detail': 'أزالك المعلّم من الحصة. اطلب رابطًا جديدًا إذا كان ذلك خطأً.',
  'evicted.detail':
    'الحصة مفتوحة الآن على جهاز آخر. أغلق هذه الصفحة، أو انضم من هنا لإرجاع الحصة إلى هذا الجهاز.',
  'error.retry': 'حاول مرة أخرى',
  'error.expired.title': 'هذا الرابط لم يعد صالحًا',
  'error.expired.detail': 'ارجع إلى تطبيق Everhall واضغط «انضم» للحصول على رابط جديد.',
  'error.notStarted.title': 'لم تبدأ حصتك بعد',
  'error.notStarted.detail': 'عد قبل موعد البداية بقليل، ثم اضغط «انضم» في تطبيق Everhall.',
  'error.ended.title': 'انتهت هذه الحصة',
  'error.ended.detail': 'ارجع إلى تطبيق Everhall لترى حصصك الأخرى.',
  'error.network.title': 'مشكلة في الاتصال',
  'error.network.detail': 'تحقق من اتصالك بالإنترنت ثم حاول مرة أخرى.',

  'error.unavailable.title': 'الحصة ليست جاهزة بعد',
  'error.unavailable.detail': 'انتظر نصف دقيقة تقريبًا ثم حاول مرة أخرى.',
  'error.unknown.title': 'حدث خطأ ما',
  'error.unknown.detail':
    'حاول مرة أخرى — وإذا استمرت المشكلة ارجع إلى تطبيق Everhall واضغط «انضم».',
  'error.reference': 'الرقم المرجعي',
  'prejoin.joining': 'جارٍ الانضمام…',
  'prejoin.camera': 'الكاميرا',
  'prejoin.microphone': 'الميكروفون',
  'prejoin.speaker': 'السمّاعة',
  'prejoin.speakerTest': 'تجربة',
  'prejoin.speakerPlaying': 'جارٍ التشغيل…',
  'prejoin.default': 'الإعداد الافتراضي',
  'prejoin.cameraOff': 'الكاميرا مغلقة',
  'prejoin.blocked': 'الكاميرا محجوبة',
  'prejoin.blockedHelp':
    'المتصفح يحجب الكاميرا والميكروفون. اسمح بهما من شريط العنوان ثم اضغط «إعادة المحاولة» — أو اضغط «انضمام» للدخول بالكاميرا مغلقة.',

  // Control labels use the masdar so they carry no gender: «إعادة المحاولة»,
  // «الانضمام», not «أعِد» / «انضم».
  'prejoin.noCamera': 'لا توجد كاميرا في هذا الجهاز — لا مشكلة. سيسمعك معلّمك.',
  'prejoin.hostMicFault': 'لن يسمعك الطلاب حتى يعمل الميكروفون. يمكنك إصلاحه بعد الانضمام.',
  'prejoin.hostMicOff': 'الميكروفون مغلق — لن يسمعك الطلاب.',
  'prejoin.hostMicChecking': 'جارٍ التحقّق من الميكروفون…',
  'prejoin.hostMicUnheard': 'لم نسمعك بعد — قل شيئًا أو اختر ميكروفونًا آخر.',
  'prejoin.fixMic': 'إصلاح الميكروفون',
  'prejoin.joinAnyway': 'الانضمام على أي حال',
  'prejoin.fixCamera': 'إصلاح الكاميرا',
  'connection.title': 'اتصالك غير مستقر',
  'connection.body':
    'قد تتجمّد الحصة أو تنقطع. الإصلاح من هنا عادةً أفضل من نقل الجميع إلى تطبيق آخر.',
  'help.title': 'فحص الإعدادات',
  'help.menu': 'هل هناك شيء لا يعمل؟',
  'help.header': 'هناك أمر يحتاج انتباهك — افتح فحص الإعدادات',
  'help.row.mic': 'الميكروفون',
  'help.row.cam': 'الكاميرا',
  'help.row.speaker': 'مكبرات الصوت',
  'help.row.share': 'مشاركة الشاشة',
  'help.row.connection': 'الاتصال',
  'help.row.pip': 'النافذة العائمة',
  'help.fix': 'إصلاح',
  'help.testTone': 'تشغيل صوت تجريبي',
  'help.copyCode': 'نسخ رمز الدعم',
  'help.readCode': 'اقرأ هذا الرمز للدعم: {code}',
  'help.ok': 'جاهز للاستخدام',
  'help.mic.muted': 'الميكروفون مغلق',
  'help.mic.blocked': 'المتصفح لا يسمح بالميكروفون',
  'help.mic.silent': 'الميكروفون لا يرسل صوتًا',
  'help.cam.off': 'الكاميرا مغلقة',
  'help.cam.blocked': 'المتصفح لا يسمح بالكاميرا',
  'help.cam.frozen': 'الكاميرا توقفت عن إرسال الفيديو',
  'help.speaker.unavailable': 'اختيار مكبر الصوت غير متاح هنا',
  'help.speaker.blocked': 'اضغط للسماح بتشغيل صوت الحصة',
  'help.share.unavailable': 'هذا الجهاز لا يستطيع مشاركة الشاشة',
  'help.share.board': 'أغلق السبورة حتى يرى الطلاب شاشتك',
  'help.share.failed': 'مشاركة الشاشة تحتاج إلى انتباه',
  'help.connection.lost': 'جارٍ إعادة اتصال الحصة',
  'help.connection.poor': 'الاتصال ضعيف',
  'help.pip.unavailable': 'النافذة العائمة غير متاحة هنا',
  'help.pip.ready': 'الحصة جاهزة لتصبح عائمة',
  'help.pip.active': 'عائمة ✓',
  'recovery.reconnectingMic': 'جارٍ إعادة توصيل الميكروفون…',
  'mic.check.title': 'هل نسمعك؟',
  'mic.check.prompt': 'قل مرحبًا — سيتحرك الشريط.',
  'mic.check.heard': 'نسمعك جيدًا.',
  'mic.check.quiet': 'لا نسمع شيئًا بعد. تحقّق من زر كتم الصوت في السماعة، أو اختر ميكروفونًا آخر.',
  'mic.check.checking': 'جارٍ التحقّق من الميكروفون…',
  'mic.status.blocked': 'الميكروفون محجوب',
  'mic.status.missing': 'لا يوجد ميكروفون',
  'mic.status.busy': 'الميكروفون غير متاح',
  'mic.status.silent': 'الميكروفون لا يُصدر صوتًا',
  'mic.status.fix': 'طريقة الحل',
  'mic.help.titleBlocked': 'المتصفح ماسك الميكروفون',
  'mic.help.titleMissing': 'ما لقينا ميكروفون',
  'mic.help.titleBusy': 'فيه برنامج تاني ماسك الميكروفون',
  'mic.help.titleSilent': 'الميكروفون شغال، بس الصوت مش واصل',
  'mic.help.lede': 'اتبع الصورة، وبعدين اضغط الزر الكبير تحت.',
  'mic.help.ledeSilent': 'شوف زر الكتم على السماعة، وبعدين اضغط الزر تحت.',
  'mic.help.retry': 'حاول مرة ثانية',
  'mic.help.checking': 'جارٍ التحقّق…',
  'mic.help.notNow': 'ليس الآن',
  'mic.help.joinAnyway': 'الانضمام بدون ميكروفون',
  'mic.help.notYourBrowser': 'ليس هذا متصفحك؟',
  'mic.help.stepsLabel': 'الخطوات',
  'mic.help.askAdult': 'ما زالت المشكلة؟ اطلب مساعدة شخص كبير، أو أخبر معلّمك في الدردشة.',
  'cam.help.titleBlocked': 'الكاميرا مغلقة',
  'cam.help.titleMissing': 'لا نجد كاميرا',
  'cam.help.titleBusy': 'الكاميرا مشغولة',
  'cam.help.lede': 'اتبع الصورة، وبعدين اضغط الزر الكبير تحت.',
  'mic.platform.chromeAndroid': 'Chrome على Android',
  'mic.platform.chromiumDesktop': 'Chrome على الكمبيوتر',
  'mic.platform.edgeDesktop': 'Edge على الكمبيوتر',
  'mic.platform.firefoxDesktop': 'Firefox على الكمبيوتر',
  'mic.platform.ios': 'iPhone أو iPad',
  'mic.platform.generic': 'أي متصفح',
  'mic.steps.chromeAndroid.1': 'اضغط على النقاط الثلاث أعلى المتصفح.',
  'mic.steps.chromeAndroid.2': 'افتح «إعدادات الموقع».',
  'mic.steps.chromeAndroid.3': 'اجعل «الميكروفون» على «السماح».',
  'mic.steps.chromiumDesktop.1': 'اضغط على الأيقونة في أول شريط العنوان.',
  'mic.steps.chromiumDesktop.2': 'شغّل «الميكروفون».',
  'mic.steps.edgeDesktop.1': 'اضغط على الأيقونة في أول شريط العنوان ثم «أذونات هذا الموقع».',
  'mic.steps.edgeDesktop.2': 'اجعل «الميكروفون» على «السماح».',
  'mic.steps.firefoxDesktop.1': 'اضغط على القفل في أول شريط العنوان.',
  'mic.steps.firefoxDesktop.2': 'أزل حجب الميكروفون ثم اسمح به.',
  'mic.steps.ios.1': 'افتح «الإعدادات»، ابحث عن هذا المتصفح، واسمح بالميكروفون.',
  'mic.steps.ios.2': 'ارجع إلى هذه الصفحة واضغط «إعادة المحاولة».',
  'mic.steps.generic.1': 'افتح إعدادات المتصفح لهذا الموقع.',
  'mic.steps.generic.2': 'اجعل «الميكروفون» على «السماح».',
  'mic.steps.missing.1': 'وصّل ميكروفونًا أو سماعة.',
  'mic.steps.missing.2': 'ثم اضغط «إعادة المحاولة».',
  'mic.steps.busy.1': 'أغلق أي تطبيق آخر يستخدم الميكروفون، مثل تطبيق مكالمات آخر أو تسجيل صوتي.',
  'mic.steps.busy.2': 'ثم اضغط «إعادة المحاولة».',
  'mic.steps.silent.1': 'افحص زر كتم الصوت الصغير الموجود على سلك السماعة أو الإيربودز.',
  'mic.steps.silent.2': 'اختر ميكروفونًا آخر من القائمة، ثم قل «مرحبًا».',
  'mic.steps.silent.3':
    'على ويندوز: افتح الإعدادات ← الخصوصية ← الميكروفون وتأكد من السماح للتطبيقات باستخدامه.',
  'cam.steps.chromeAndroid.1': 'اضغط على النقاط الثلاث أعلى المتصفح.',
  'cam.steps.chromeAndroid.2': 'افتح «إعدادات الموقع».',
  'cam.steps.chromeAndroid.3': 'اجعل «الكاميرا» على «السماح».',
  'cam.steps.chromiumDesktop.1': 'اضغط على الأيقونة في أول شريط العنوان.',
  'cam.steps.chromiumDesktop.2': 'شغّل «الكاميرا».',
  'cam.steps.edgeDesktop.1': 'اضغط على الأيقونة في أول شريط العنوان ثم «أذونات هذا الموقع».',
  'cam.steps.edgeDesktop.2': 'اجعل «الكاميرا» على «السماح».',
  'cam.steps.firefoxDesktop.1': 'اضغط على القفل في أول شريط العنوان.',
  'cam.steps.firefoxDesktop.2': 'أزل حجب الكاميرا ثم اسمح بها.',
  'cam.steps.ios.1': 'افتح «الإعدادات»، ابحث عن هذا المتصفح، واسمح بالكاميرا.',
  'cam.steps.ios.2': 'ارجع إلى هذه الصفحة واضغط «إعادة المحاولة».',
  'cam.steps.generic.1': 'افتح إعدادات المتصفح لهذا الموقع.',
  'cam.steps.generic.2': 'اجعل «الكاميرا» على «السماح».',
  'cam.steps.missing.1': 'وصّل كاميرا.',
  'cam.steps.missing.2': 'ثم اضغط «إعادة المحاولة».',
  'cam.steps.busy.1': 'أغلق أي تطبيق آخر يستخدم الكاميرا، مثل تطبيق مكالمات آخر.',
  'cam.steps.busy.2': 'ثم اضغط «إعادة المحاولة».',
  'roster.micBlocked': 'المتصفح يحجب الميكروفون عنده',
  'roster.micMissing': 'لا يوجد ميكروفون على جهازه',
  'roster.micBusy': 'الميكروفون عنده مشغول أو غير متاح',
  'roster.micSilent': 'الميكروفون عنده مفتوح لكن لا يصل منه صوت',
  'tile.micBlockedLabel': 'الميكروفون محجوب',
  'tile.micHelpHint':
    'اطلب منه السماح بالميكروفون لهذا الموقع، ثم الضغط على «إعادة المحاولة» على شاشته.',
  'prejoin.retry': 'إعادة المحاولة',
  'prejoin.classLabel': 'الحصة',
  'prejoin.micOn': 'إيقاف الميكروفون',
  'prejoin.micOff': 'تشغيل الميكروفون',
  'prejoin.camOn': 'إيقاف الكاميرا',
  'prejoin.camOff': 'تشغيل الكاميرا',
  'role.student': 'طالب',
  'role.teacher': 'المعلّم',
  'role.parent': 'ولي الأمر',
  'room.recording': 'جارٍ التسجيل',
  'room.observing': 'مراقبة خفية',
  'room.controls': 'أدوات الحصة',
  'more.open': 'المزيد',
  'more.close': 'إغلاق المزيد',
  'more.caption': 'المزيد',
  'more.controls': 'أدوات إضافية',
  'more.tools': 'الأدوات',
  'devices.heading': 'الأجهزة',
  'devices.microphone': 'الميكروفون',
  'devices.camera': 'الكاميرا',
  'devices.speaker': 'مكبر الصوت',
  'devices.systemDefault': 'إعداد النظام الافتراضي',
  'devices.useMic': 'استخدام الميكروفون: {device}',
  'devices.useCamera': 'استخدام الكاميرا: {device}',
  'devices.useSpeaker': 'استخدام مكبر الصوت: {device}',
  'devices.nowUsing': 'يُستخدم الآن {device}.',
  'devices.switchFailed': 'تعذّر تبديل الجهاز. حاول مرة أخرى.',
  'devices.deviceGone': 'الجهاز المحدد لم يعد متاحًا. نستخدم إعداد النظام الافتراضي.',
  'more.openHands': 'المزيد — {count} أيادٍ مرفوعة',
  'more.closeHands': 'إغلاق المزيد — {count} أيادٍ مرفوعة',
  'more.openUpdates': 'المزيد — توجد تحديثات',
  'more.closeUpdates': 'إغلاق المزيد — توجد تحديثات',
  'room.react': 'إرسال تفاعل',
  'room.reactClose': 'إغلاق التفاعلات',
  'room.inviteLink': 'رابط الدعوة',
  'room.copyInvite': 'نسخ رابط الدعوة',
  'room.inviteCopied': 'تم نسخ الرابط',
  'room.raiseHand': 'رفع اليد',
  'room.lowerHand': 'إنزال اليد',
  'room.handRaised': 'يد مرفوعة',
  'room.handsUp': 'أيادٍ مرفوعة',
  'audio.blocked': 'اضغط لسماع الحصة.',
  'audio.unlock': 'تشغيل الصوت',
  'chat.title': 'دردشة الحصة',
  'chat.open': 'فتح الدردشة',
  'chat.close': 'إغلاق الدردشة',
  'chat.openUnread': 'فتح الدردشة — {count} جديدة',
  'chat.placeholder': 'اكتب رسالة…',
  'chat.send': 'إرسال',
  'chat.emoji': 'إضافة إيموجي',
  'chat.empty': 'لا رسائل بعد — قل مرحبًا!',
  'chat.earlier': 'الرسائل الأقدم',
  'chat.new': 'رسائل جديدة',
  'chat.removed': 'تمت إزالتها',
  'chat.removedByYou': 'حذفتها أنت',
  'chat.delete': 'حذف هذه الرسالة',
  'chat.muteUser': 'إيقاف الدردشة',
  'chat.unmuteUser': 'السماح بالدردشة مجددًا',
  'chat.mutedChip': 'الدردشة موقوفة',
  'chat.freeze': 'إيقاف الدردشة مؤقتًا',
  'chat.unfreeze': 'استئناف الدردشة',
  'chat.frozenHost': 'الدردشة موقوفة مؤقتًا — الكتابة متاحة لك فقط.',
  'chat.frozenStudent': 'الدردشة موقوفة مؤقتًا.',
  'chat.mutedYou': 'دردشتك موقوفة مؤقتًا.',
  'chat.phFrozen': 'الدردشة موقوفة',
  'chat.phMuted': 'دردشتك موقوفة',
  'chat.tooLong': 'الرسالة طويلة جدًا.',
  'chat.tooFast': 'على مهل — رسالة واحدة في كل مرة.',
  'chat.observer': 'للقراءة فقط — مشاهدة',
  'chat.attach': 'إضافة صورة أو ملف',
  'chat.imageView': 'صورة',
  'chat.imgTooLarge': 'الصورة كبيرة جدًا — جرّب صورة أصغر.',
  'chat.imgNotImage': 'هذا ليس صورة.',
  'chat.imgTimedOut': 'استغرقت وقتًا طويلًا — جرّب صورة أصغر.',
  'chat.imgUnreadable': 'تعذّر فتح هذه الصورة — جرّب صورة أخرى.',
  'chat.imgFailed': 'تعذّر إرسال الصورة — اضغط للمحاولة مجددًا.',
  'chat.imgBusy': 'صورة واحدة في كل مرة — انتظر انتهاء هذه الصورة.',
  'chat.imgAnimated': 'لا يمكن إرسال الصور المتحركة — جرّب صورة ثابتة أو لقطة شاشة.',
  'chat.fileTooLarge': 'الملف كبير جدًا — الحد الأقصى 10 ميغابايت.',
  'chat.notFile': 'يمكن إرسال الصور وملفات PDF فقط.',
  'chat.fileOpen': 'فتح الملف',
  'chat.fileFailed': 'تعذّر إرسال الملف — اضغط للمحاولة مجددًا.',
  'chat.fileOpenFailed': 'تعذّر فتح الملف — اضغط عليه مرة أخرى.',
  'chat.drop': 'أفلت الصور أو الملفات هنا',
  'chat.dropHint': 'سيتم إرسالها إلى الحصة',
  'chat.dropTooMany': '{count} فقط في المرة الواحدة — لم تُضف البقية.',
  'chat.sending': 'جارٍ إرسال {count}…',
  'chat.playOn': 'تشغيل على {site}',
  'chat.opensOutside': 'يفتح في تبويب جديد',
  'chat.shareToPlay': 'لتشغيله للحصة، شارك شاشتك مع الصوت.',
  'chat.search': 'البحث في الرسائل',
  'chat.searchPlaceholder': 'ابحث في هذه الدردشة…',
  'chat.searchNone': 'لا نتائج لـ «{term}».',
  'chat.searchCount': '{count} نتيجة',
  'share.asked': 'هل يمكنك مشاركة شاشتك مع الحصة؟',
  'share.accept': 'مشاركة الشاشة',
  'share.tryAgain': 'حاول مرة أخرى',
  'share.close': 'إغلاق',
  'share.errUnfocused': 'اضغط «مشاركة الشاشة» مرة أخرى وشاشة الحصة أمامك.',
  'share.errUnsupported': 'هذا الجهاز لا يستطيع مشاركة الشاشة. الكمبيوتر يستطيع!',
  'share.errBlocked': 'شيء ما منع المشاركة. أغلق التطبيقات الأخرى وحاول مجددًا.',
  'share.errSystemDenied': 'الكمبيوتر لا يسمح بمشاركة الشاشة بعد.',
  'share.errBusy': 'هناك مشاركة جارية الآن — يمكنك المحاولة عندما تنتهي.',
  'share.errFailed': 'لم تنجح المشاركة. يمكنك المحاولة مرة أخرى.',
  'share.otherPresenting': 'جارٍ عرض شاشة {name}',
  'share.takeOver': 'المشاركة بدلًا من ذلك',
  // Both phrased around the action rather than at the person (تُعاد / استخدام,
  // not أعِد / استخدِم): copy rule 5 — a female teacher must not be addressed as
  // male, and the imperative in Arabic cannot avoid carrying gender.
  'share.noSound':
    'هذه المشاركة بلا صوت. لكي تسمع الحصة صوت الفيديو، تُشارَك علامة التبويب التي يعمل فيها الفيديو مع تشغيل خيار الصوت الخاص بها.',
  'share.help.title': 'إصلاح مشاركة الشاشة',
  'share.help.lede': 'اتبع هذه الخطوات ثم حاول المشاركة مرة أخرى.',
  'share.help.retry': 'مشاركة الشاشة',
  'share.help.show': 'أرني الطريقة',
  'share.help.system.1': 'افتح «إعدادات النظام».',
  'share.help.system.2': 'افتح «الخصوصية والأمان»، ثم «تسجيل الشاشة».',
  'share.help.system.3': 'فعّل المتصفح.',
  'share.help.system.4': 'أغلق المتصفح وافتحه من جديد. رابط الحصة ما زال يعمل بعد الفتح.',
  'share.help.unfocused': 'اضغط نافذة الحصة أولًا، ثم شارك الشاشة.',
  'share.help.sound.1': 'اختر «علامة تبويب Chrome» أو «علامة تبويب المتصفح».',
  'share.help.sound.2': 'فعّل «مشاركة صوت علامة التبويب أيضًا».',
  'share.help.black': 'أغلق السبورة حتى يرى الطلاب شاشتك.',
  'share.help.capture': 'أغلق تطبيق مكالمات آخر أو تطبيق التقاط آخر، ثم حاول مجددًا.',
  'share.help.capture.generic': 'أغلق تطبيقًا آخر قد يشارك الشاشة، ثم حاول مجددًا.',
  'share.soundOn':
    'الحصة تسمع صوت المشاركة الآن. استخدام سماعة رأس يحافظ على وضوح الصوت أثناء تشغيله.',
  'share.stoppedByTeacher': 'توقفت مشاركة شاشتك. يمكن مشاركتها مرة أخرى في أي وقت تحتاجه الحصة.',
  'share.hideFaces': 'إخفاء شريط الكاميرات لتأخذ المشاركة الشاشة كلها',
  'share.showFaces': 'إظهار شريط الكاميرات مرة أخرى',
  'share.fullscreen': 'عرض الشاشة المشاركة في وضع ملء الشاشة',
  'share.fullscreenShort': 'ملء الشاشة',
  'share.exitFullscreen': 'الخروج من ملء الشاشة',
  'share.exitFullscreenShort': 'خروج',
  // بدون «ال» التعريف عمدًا: القياس بخط سومار على 52 بكسل — الكلمة الأقصر
  // المقروءة أفضل من كلمة مقطوعة بثلاث نقاط.
  'ctl.mic': 'مايك',
  'ctl.camera': 'كاميرا',
  'ctl.saver': 'توفير',
  'ctl.bg': 'خلفية',
  'ctl.react': 'تفاعل',
  'ctl.hand': 'اليد',
  'ctl.share': 'مشاركة',
  'ctl.pip': 'نافذة',
  'ctl.chat': 'دردشة',
  'ctl.people': 'حضور',
  'pip.enter': 'اجعل الحصة عائمة',
  'pip.exit': 'إغلاق النافذة العائمة',
  'pip.title': 'Everhall — حصتك',
  'pip.emptyTitle': 'حصتك ما زالت هنا',
  'pip.emptyBody': 'لا أحد على الكاميرا الآن — ما زال بإمكانك سماع كل شيء.',
  'pip.loading': 'جارٍ تحميل الفيديو…',
  'pip.steps.chromium.1': 'اضغط الأيقونة في أول شريط العنوان، ثم «إعدادات الموقع».',
  'pip.steps.chromium.2': 'ابحث عن «نافذة صورة داخل صورة تلقائية» واختر «السماح».',
  'pip.steps.generic.1': 'اضغط «اجعل الحصة عائمة» قبل تغيير النافذة.',
  'pip.autoBlockedTitle': 'لن تطفو الحصة تلقائيًا.',
  'pip.tipTitle': 'نصيحة: عند تغيير التبويب، تطفو الحصة في نافذة صغيرة.',
  'pip.tipBody': 'هل ستنتقل لتطبيق آخر؟ اضغط «اجعل الحصة عائمة» أولًا.',
  'pip.tipFloatNow': 'اجعلها عائمة الآن',
  'pip.tipGotIt': 'حسنًا',
  'moderate.askShare': 'طلب مشاركة الشاشة',
  'moderate.askedShare': 'تم إرسال الطلب',
  'moderate.shareWaiting': 'في انتظار الرد…',
  'moderate.shareBusy': 'هناك مشاركة جارية بالفعل',
  'moderate.shareUnavailable': 'هذا الجهاز لا يستطيع مشاركة الشاشة',
  'moderate.shareAccepted': 'جارٍ عرض شاشة {name}',
  'moderate.shareDeclined': '{name}: «ليس الآن»',
  'moderate.shareNoAnswer': 'لا يوجد رد من {name}',
  'moderate.shareCancelled': 'لم يتم اختيار شاشة — {name}',
  'moderate.shareFailed': 'لم تنجح المشاركة على هذا الجهاز',
  'roster.title': 'في هذه الحصة',
  'roster.open': 'إظهار المشاركين',
  'roster.hide': 'إخفاء المشاركين',
  'roster.openCount': 'إظهار المشاركين — {count} في الحصة',
  'roster.close': 'إغلاق',
  'roster.handsUp': '{count} في انتظار الحديث',
  'roster.speaking': 'يتحدث الآن',
  'roster.micOn': 'الميكروفون مفتوح',
  'roster.cameraOn': 'الكاميرا مفتوحة',
  'room.share': 'مشاركة الشاشة',
  'room.stopShare': 'إيقاف المشاركة',
  'room.presenting': 'مشاركة الشاشة',
  'room.celebrate': 'احتفال',
  'room.wellDone': 'أحسنت!',
  'bg.title': 'الخلفية',
  'bg.open': 'تغيير الخلفية',
  'bg.close': 'إغلاق الخلفيات',
  'bg.none': 'بدون',
  'bg.blur': 'تمويه',
  'bg.everhall': 'Everhall',
  'bg.study': 'مذاكرة',
  'bg.sky': 'سماء',
  'bg.mint': 'نعناع',
  'bg.space': 'فضاء',
  'bg.applying': 'جارٍ التطبيق…',
  'bg.slow': 'قد تعمل الخلفيات ببطء على هذا الجهاز.',
  'bg.unsupported': 'الخلفيات غير مدعومة في هذا المتصفح.',
  'room.you': 'أنت',
  'room.leave': 'مغادرة الحصة',
  'room.leaveTitle': 'هل تريد مغادرة الحصة؟',
  'room.leaveBody': 'سيرى معلّمك وزملاؤك أنك غادرت. يمكنك الرجوع بنفس الرابط ما دامت الحصة مستمرة.',
  'room.leaveStay': 'البقاء في الحصة',
  'room.leaveYes': 'نعم، أريد المغادرة',
  'room.endForAll': 'إنهاء الاجتماع للجميع',
  'room.endTitle': 'هل تريد إنهاء الاجتماع للجميع؟',
  'room.endBody': 'سيُفصل الجميع ويتوقف رابط الدعوة عن العمل.',
  'room.endYes': 'نعم، إنهاء الاجتماع',
  'room.endKeep': 'متابعة الاجتماع',
  'lobby.waiting': '{count} بانتظار الدخول',
  'lobby.guest': 'ضيف',
  'lobby.admit': 'السماح بالدخول',
  'lobby.deny': 'رفض الدخول',
  'lobby.admitName': 'السماح بدخول {name}',
  'lobby.denyName': 'رفض دخول {name}',
  'lobby.admitAll': 'السماح للجميع بالدخول',
  'room.waitingStudents': 'في انتظار انضمام الطلاب',
  'room.muted': 'الميكروفون مغلق',
  'room.mutedSpeaking': 'صوتك مكتوم',
  'room.blurStarting': 'جارٍ تحضير الخلفية…',
  'room.blurFailed': 'لم تبدأ الخلفية — الكاميرا ما زالت تعمل',
  'room.hideFaces': 'إخفاء الوجوه',
  'room.showFaces': 'إظهار الوجوه',
  'room.reconnecting': 'جارٍ إعادة الاتصال… ابقَ على هذه الشاشة',
  'room.micBanner.host': 'لا يسمعك الطلاب — الميكروفون لا يعمل.',
  'room.micBanner.student': 'لا يسمعك المعلّم — الميكروفون لا يعمل.',
  'room.fix': 'إصلاح',
  'room.dismiss': 'إخفاء',
  'saver.control': 'تشغيل توفير البيانات',
  'saver.controlOn': 'إيقاف توفير البيانات',
  'saver.controlAuto': 'تفعّل توفير البيانات تلقائيًا — اضغط لمعرفة السبب',
  'saver.onManual': 'نوفر البيانات — أوقفنا الفيديوهات الواردة ليبقى الصوت واضحًا.',
  'saver.turnOff': 'إيقاف',
  'saver.resume': 'إعادة تشغيل الفيديو',
  'saver.autoL1': 'نوفر البيانات — أوقفنا فيديوهات الزملاء ليبقى الصوت واضحًا.',
  'saver.autoL2':
    'نوفر البيانات — أوقفنا الفيديوهات الواردة ليبقى الصوت واضحًا، وستعود عند تحسّن الاتصال.',
  'saver.cameraStillOn': 'كاميرتك ما زالت تعمل.',
  'saver.dismiss': 'إخفاء',
  'saver.pausedTile': 'الفيديو متوقف لتوفير البيانات',
  'saver.weakPeer': 'اتصالهم ضعيف — ما زالوا يسمعونك',
  'tile.connectionIssue': 'مشكلة في الاتصال — الصوت مستمر.',
  'nc.control': 'إلغاء الضوضاء',
  'nc.on': 'إيقاف إلغاء الضوضاء',
  'nc.off': 'تشغيل إلغاء الضوضاء — يحجب ضوضاء ما حولك',
  'nc.loading': 'جارٍ تشغيل إلغاء الضوضاء…',
  'nc.failed': 'تعذّر تشغيل إلغاء الضوضاء — الميكروفون العادي قيد الاستخدام. اضغط للمحاولة مجددًا.',
  'nc.stateOn': 'يعمل',
  'nc.stateOff': 'متوقف',
  'nc.stateLoading': 'جارٍ التشغيل…',
  'nc.stateFailed': 'لم يعمل',
  'nc.toastOn': 'إلغاء الضوضاء يعمل الآن — تُرشَّح الأصوات من حولك ولا يصل للطلاب سوى صوتك.',
  'nc.toastOnStudent': 'إلغاء الضوضاء يعمل الآن — تُرشَّح الأصوات من حولك من ميكروفونك.',

  'moderate.for': 'إدارة {name}',
  'moderate.remove': 'إزالة من الحصة',
  'moderate.muteMic': 'إيقاف الميكروفون',
  'moderate.cameraOff': 'إيقاف الكاميرا',
  'moderate.stopShare': 'إيقاف مشاركة الشاشة',
  'moderate.askMic': 'طلب تشغيل الميكروفون',
  'moderate.askCamera': 'طلب تشغيل الكاميرا',
  'moderate.askNote': 'الإيقاف يحدث فورًا، وإعادة التشغيل تبقى قرار الطالب.',
  'moderate.askedMic': 'تم إرسال طلب فتح الميكروفون',
  'moderate.askedCamera': 'تم إرسال طلب فتح الكاميرا',
  'moderate.failed': 'لم تنجح العملية — حاول مرة أخرى',

  'unmute.askedMic': 'يُرجى تشغيل الميكروفون من أجل الحصة',
  'unmute.askedCamera': 'يُرجى تشغيل الكاميرا من أجل الحصة',
  'unmute.turnOnMic': 'تشغيل الميكروفون',
  'unmute.turnOnCamera': 'تشغيل الكاميرا',
  'unmute.failedMic': 'تعذّر تشغيل الميكروفون. تأكد من توصيله والسماح له.',
  'unmute.failedCamera': 'تعذّر تشغيل الكاميرا. تأكد من السماح لها.',
  'unmute.notNow': 'ليس الآن',

  'waiting.title': 'أنت في الحصة! في انتظار معلّمك…',
  'waiting.sub': 'سيصل معلّمك في أي لحظة.',
  'waiting.hint1': 'نصيحة: اجلس في مكان هادئ حتى يسمعك الجميع.',
  'waiting.hint2': 'نصيحة: اضغط زر اليد عندما تريد الإجابة.',
  'waiting.hint3': 'نصيحة: اجعل الكشكول والقلم قريبين منك.',
  'waiting.hint4': 'نصيحة: يمكنك إرسال 👍 أو 🎉 لزملائك.',

  'surface.board': 'السبورة',
  'surface.openBoard': 'فتح السبورة',
  'surface.closeBoard': 'إغلاق السبورة',
  'surface.clearBoard': 'مسح السبورة',
  'surface.clearShort': 'مسح',
  'surface.boardLoading': 'جارٍ تحميل السبورة…',
  'surface.shareConnecting': 'جارٍ الاتصال بالشاشة المشتركة…',
  'surface.viewOnly': 'عرض فقط',
  'surface.ink': 'رسم',
  'surface.startInk': 'الرسم على الشاشة المشتركة',
  'surface.stopInk': 'إيقاف الرسم',
  'surface.annotating': 'رسم',
  'surface.pen': 'قلم',
  'surface.highlighter': 'مظلّل',
  'surface.undo': 'تراجع',
  'surface.clearInk': 'مسح الرسومات',
  'surface.boardTooLarge': 'السبورة ممتلئة ولا يمكن مشاركتها — امسحها للمتابعة',
  'surface.boardFailed': 'تعذّرت مشاركة السبورة — جرّب مسحها',
  'surface.boardFailedToLoad': 'تعذّر تحميل السبورة. تحقّق من الاتصال وحاول مرة أخرى.',
  'surface.tools': 'أدوات الرسم',
  'surface.arrow': 'سهم',
  'surface.line': 'خط',
  'surface.rect': 'مستطيل',
  'surface.ellipse': 'دائرة',
  'surface.eraser': 'ممحاة',
  'surface.color': 'اللون',
}

/**
 * Keys come from the English catalogue, so a typo or an unadded string is a
 * compile error rather than a raw key rendered to a child mid-lesson — which is
 * exactly how `room.leave` reached production showing "room.leave".
 */
export type StringKey = keyof typeof en

export const CATALOGUES: Record<Locale, Record<StringKey, string>> = { ar, en }

/**
 * A live class or an Everhall Live meeting (`session.kind`, ADR 0013). Meetings read host,
 * participant and guest copy from meetingCopy.ts. The `?kind=` hint covers the boot screens before
 * the exchange answers; the exchange's `session.kind` then decides. Anything else is a class.
 */
type SessionKind = 'class' | 'meeting'
const kindOf = (kind: unknown): SessionKind => (kind === 'meeting' ? 'meeting' : 'class')
let sessionKind = kindOf(new URLSearchParams(window.location.search).get('kind'))

export function setSessionKind(kind: unknown): void {
  sessionKind = kindOf(kind)
}

export const isMeeting = (): boolean => sessionKind === 'meeting'

/**
 * Locale comes from `?lang=`, then the browser, then Arabic. The main app can
 * therefore hand off the student's chosen language explicitly, and a shared
 * device still defaults to the majority language rather than English.
 */
export function resolveLocale(): Locale {
  const requested = new URLSearchParams(window.location.search).get('lang')
  if (requested === 'ar' || requested === 'en') return requested
  return navigator.language?.toLowerCase().startsWith('en') ? 'en' : 'ar'
}

export const locale: Locale = resolveLocale()
export const isRTL = locale === 'ar'

export function t(key: StringKey): string {
  if (sessionKind === 'meeting') {
    const meeting = meetingCopy[locale][key]
    if (meeting !== undefined) return meeting
  }
  return CATALOGUES[locale][key] ?? en[key] ?? key
}

/** Applied once at boot so LiveKit's prefabs and our own CSS both mirror. */
export function applyDocumentLocale(): void {
  document.documentElement.lang = locale
  document.documentElement.dir = isRTL ? 'rtl' : 'ltr'
  document.title = t('app.title')
}
